import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Lock } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Chapter, Toc } from "@/components/site/BookContent";
import { http } from "@/lib/api";
import { PackageOffer } from "@/components/site/PackageOffer";
import { BuyForm } from "@/components/site/BookCheckout";
import { fileUrl } from "@/lib/images";

const Cover = ({ b, token }) => (
  <div className="relative aspect-[2/3] w-full max-w-sm overflow-hidden shadow-2xl" data-testid="book-cover">
    {b.cover_path && <img src={fileUrl(b.cover_path, token)} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />}
    <div className="absolute inset-x-0 top-0 p-8 text-center bg-gradient-to-b from-[#FDFBF7]/90 via-[#FDFBF7]/60 to-transparent">
      <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[#8C6E48]">SømSignature</p>
      <p className="font-serif-display text-4xl leading-none mt-3">{b.title}</p>
      <p className="text-xs mt-3 text-[#5C5A56]">{b.subtitle}</p>
    </div>
    <p className="absolute bottom-5 inset-x-0 text-center font-serif-display text-lg text-[#FDFBF7] drop-shadow">{b.author}</p>
  </div>
);

export function BookProduct() {
  const [b, setB] = useState(null);
  useEffect(() => { http.get("/public/books").then((r) => setB(r.data[0] || false)); }, []);
  if (b === null) return <div className="p-24 text-sm">Laster…</div>;
  if (!b) return <div className="p-24" data-testid="book-none">Håndboken kommer snart.</div>;
  return (
    <div data-testid="book-product-page">
      <section className="px-6 md:px-16 lg:px-24 pt-16 pb-20 grid lg:grid-cols-12 gap-14 items-center">
        <Reveal className="lg:col-span-5 flex justify-center"><Cover b={b} /></Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <p className="eyebrow mb-5">Digital håndbok · PDF og nettversjon</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">{b.title}</h1>
          <p className="mt-4 text-lg text-[#8C6E48] font-serif-display italic">{b.subtitle}</p>
          <p className="mt-6 text-[#5C5A56] leading-relaxed max-w-xl">{b.description}</p>
          <p className="mt-4 text-sm max-w-xl">{b.for_whom}</p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <p className={b.price ? "font-serif-display text-5xl" : "font-serif-display text-2xl italic text-[#8C6E48]"} data-testid="book-price">{b.price || "Pris kunngjøres ved lansering"}</p>
            {b.payment_active ? (
              <BuyForm slug={b.slug} />
            ) : (
              <Link to="/bestill?type=bok#forespørsel" className="px-8 py-4 bg-[#1A1A1A] text-[#FDFBF7] text-sm hover:bg-[#8C6E48] transition-colors" data-testid="book-interest-btn">Meld interesse – få beskjed ved lansering</Link>
            )}
          </div>
          {!b.payment_active && <p className="mt-3 text-xs text-[#5C5A56]" data-testid="book-payment-note">Nettbetaling åpner snart. Meld interesse, så får du tilgangslenke på e-post når boken er klar.</p>}
          <p className="mt-6 text-sm text-[#5C5A56]">Håndboken er under arbeid og lanseres når alle kapitler er faglig gjennomgått av Liisa.</p>
        </Reveal>
      </section>
      <PackageOffer />
      <section className="px-6 md:px-16 lg:px-24 py-16 bg-[#F0EBE1] grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><p className="eyebrow mb-3">Innhold</p><h2 className="text-4xl font-medium">Fem kapitler, steg for steg</h2></div>
        <div className="lg:col-span-7 lg:col-start-6"><Toc chapters={b.chapters} /></div>
      </section>
      <div className="px-6 md:px-16 lg:px-24">
        {b.chapters.map((c) => <Chapter key={c.id} c={c} />)}
        <div className="py-16 flex items-center gap-4 text-sm text-[#5C5A56]"><Lock className="w-4 h-4" strokeWidth={1.5} />Hele håndboken med alle bildeserier er tilgjengelig etter kjøp.</div>
      </div>
    </div>
  );
}

export function BookReader() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get(`/books/read/${token}`).then((r) => setD(r.data)).catch(() => setErr("Lenken er ugyldig eller utløpt. Kontakt oss om du trenger hjelp.")); }, [token]);
  if (err) return <div className="p-24" data-testid="book-reader-error">{err}</div>;
  if (!d) return <div className="p-24 text-sm">Laster håndboken…</div>;
  const b = d.book;
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="book-reader">
      <header className="no-print sticky top-0 z-40 backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#E5E0D8] px-6 md:px-16 h-16 flex items-center justify-between">
        <Link to="/" className="font-serif-display text-xl">SømSignature</Link>
        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm" data-testid="book-pdf-btn"><Download className="w-4 h-4" strokeWidth={1.5} />Lagre som PDF</button>
      </header>
      <section className="px-6 md:px-16 lg:px-24 py-16 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5 flex justify-center"><Cover b={b} token={token} /></div>
        <div className="lg:col-span-7">
          <p className="text-sm text-[#5C5A56]">Lisensiert til {d.buyer}</p>
          <h2 className="text-3xl font-medium mt-6 mb-6">Innhold</h2>
          <Toc chapters={b.chapters} />
        </div>
      </section>
      <div className="px-6 md:px-16 lg:px-24 pb-24">{b.chapters.map((c) => <Chapter key={c.id} c={c} token={token} />)}</div>
    </div>
  );
}
